'use client'

import { cn } from '@brew/ui/cn'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  adoptMeasuredEmailWidth,
  parseEmailHtmlContentWidth,
} from '@/lib/rendering/email-html-width'
import {
  computeScaledHeight,
  computeScaleToFit,
} from './email-jsx-preview-scaler'
import { PixelPerfectEmailFrame } from './pixel-perfect-email-frame'

const DEFAULT_DESIGN_WIDTH = 600

export interface ScaledEmailHtmlFrameProps {
  /** The exact persisted/delivered HTML document. */
  html: string
  title: string
  /**
   * When true (default), render the document at its own design width and
   * downscale-only via `transform: scale(...)` so it fits the host without
   * reflowing — the frame's internal viewport stays at the email's true
   * width, so the document's media queries never see the host's width.
   * Set to false for hosts that size the frame themselves.
   */
  fitToWidth?: boolean
  /**
   * Override the design width. When omitted it is parsed from the HTML
   * (`parseEmailHtmlContentWidth`) with a 600px fallback.
   */
  intrinsicWidth?: number
  /** Outer wrapper className. */
  className?: string
  /** Outer wrapper style overrides (merged with the computed wrapper style). */
  style?: CSSProperties
  /**
   * Height reserved until the frame reports its first measurement, so the
   * host layout doesn't collapse-then-jump while the document loads.
   */
  unmeasuredMinHeight?: number
  /**
   * Observes the resolved design width (parsed guess, then any live
   * measurement adopted over it) so hosts can size their own chrome.
   */
  onIntrinsicWidthResolved?: (width: number) => void
}

/**
 * Read-only, fit-to-width presentation of a stored email HTML document.
 *
 * Composition wrapper only: the document itself renders through an
 * untouched `PixelPerfectEmailFrame` (hardened srcDoc, self-measured
 * height). This component pins the frame to the email's design width and
 * scales the result down to the host column — the HTML twin of
 * `EmailJsxPreview`'s `fitToWidth` behavior.
 */
export function ScaledEmailHtmlFrame({
  html,
  title,
  fitToWidth = true,
  intrinsicWidth: explicitWidth,
  className,
  style,
  unmeasuredMinHeight,
  onIntrinsicWidthResolved,
}: ScaledEmailHtmlFrameProps) {
  const fallbackDesignWidth = useMemo(() => {
    if (explicitWidth != null && explicitWidth > 0) {
      return explicitWidth
    }
    return parseEmailHtmlContentWidth(html) ?? DEFAULT_DESIGN_WIDTH
  }, [explicitWidth, html])

  // Live measurement adopted over the parsed guess, keyed to the document it
  // was measured from — this component does not remount per document, so an
  // adoption must never survive an `html` swap.
  const [measured, setMeasured] = useState<{
    html: string
    width: number
  } | null>(null)
  const adoptedWidth = measured?.html === html ? measured.width : null
  const designWidth =
    explicitWidth != null && explicitWidth > 0
      ? explicitWidth
      : (adoptedWidth ?? fallbackDesignWidth)

  // The frame reports scrollWidth at the pinned viewport — an up-only
  // overflow signal. Adoption re-pins the frame at the wider width, after
  // which the next report equals the adopted value and settles.
  const handleContentWidthChange = useCallback(
    (reportedWidth: number) => {
      setMeasured((current) => {
        const active = current?.html === html ? current.width : null
        const base = active ?? fallbackDesignWidth
        const adopted = adoptMeasuredEmailWidth(reportedWidth, base)
        if (adopted === base) {
          return current
        }
        return { html, width: adopted }
      })
    },
    [html, fallbackDesignWidth]
  )

  useEffect(() => {
    onIntrinsicWidthResolved?.(designWidth)
  }, [designWidth, onIntrinsicWidthResolved])

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [parentWidth, setParentWidth] = useState<number>(0)
  const [contentHeight, setContentHeight] = useState<number>(0)

  // Same measurement discipline as `EmailJsxPreview`: `offsetWidth` for the
  // initial read (layout box, unaffected by the scale transform), then
  // `ResizeObserver.contentRect` for updates.
  const observeWrapper = useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node
    if (!node) {
      return
    }
    setParentWidth(node.offsetWidth)
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) {
        return
      }
      setParentWidth(entry.contentRect.width)
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [])

  // Unscaled document height, reported by the frame's own measurement
  // (collapse → scrollHeight → settle). The wrapper reserves the scaled
  // equivalent because the transformed frame is out of layout flow.
  const handleHeightChange = useCallback((height: number) => {
    setContentHeight(height)
  }, [])

  const { scale } = useMemo(
    () =>
      fitToWidth
        ? computeScaleToFit({ parentWidth, intrinsicWidth: designWidth })
        : { scale: 1, fits: true },
    [fitToWidth, parentWidth, designWidth]
  )

  const wrapperStyle = useMemo<CSSProperties>(() => {
    const merged: CSSProperties = {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      ...style,
    }
    if (contentHeight > 0) {
      merged.height = computeScaledHeight(contentHeight, scale)
    } else if (unmeasuredMinHeight != null) {
      merged.minHeight = unmeasuredMinHeight
    }
    return merged
  }, [style, contentHeight, scale, unmeasuredMinHeight])

  const scalerStyle = useMemo<CSSProperties>(() => {
    if (!fitToWidth) {
      return { width: '100%' }
    }
    return {
      width: designWidth,
      // Center when the column is wider than the email; when scaled, the
      // layout box exceeds the parent so the margins collapse to zero and
      // the scaled visual fills the parent exactly.
      marginInline: 'auto',
      transformOrigin: 'top left',
      transform: scale === 1 ? undefined : `scale(${scale})`,
    }
  }, [fitToWidth, designWidth, scale])

  return (
    <div
      ref={observeWrapper}
      className={cn('scaled-email-html-frame', className)}
      style={wrapperStyle}
    >
      <div style={scalerStyle}>
        <PixelPerfectEmailFrame
          html={html}
          title={title}
          onHeightChange={handleHeightChange}
          onContentWidthChange={handleContentWidthChange}
        />
      </div>
    </div>
  )
}
